import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  PanelLeftClose,
  PanelLeft,
  Search,
  Plus,
  Bell,
  Clock,
  Loader2,
} from "lucide-react";

export const Header = ({
  // Sidebar State
  isSidebarOpen = true,
  onToggleSidebar,
  title = "Dashboard",

  // Search Props
  searchTerm,
  setSearchTerm,
  searchRef,
  searchLoading,
  searchResults = [],
  showDropdown,
  setShowDropdown,
  highlightedIndex,
  setHighlightedIndex,
  handleSelectBook,

  // User Profile
  userInitials = "EN",
}) => {
  const navigate = useNavigate();

  return (
    <header className="w-full bg-[#181512] border-b border-[#26211b] px-6 py-3.5 flex items-center justify-between gap-4 sticky top-0 z-40">
      {/* Title & Navigation Toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="text-stone-400 hover:text-stone-200 p-1.5 rounded-lg hover:bg-[#231e18] transition flex items-center justify-center"
          title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
        >
          {isSidebarOpen ? (
            <PanelLeftClose className="w-5 h-5" />
          ) : (
            <PanelLeft className="w-5 h-5" />
          )}
        </button>

        <h2 className="text-sm font-serif font-bold text-stone-100 tracking-wider">
          {title}
        </h2>

        <span className="text-xs font-medium text-stone-400 bg-[#231e18] border border-[#2e2720] px-2.5 py-1 rounded-md hidden sm:inline-block">
          {new Date().toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}
        </span>
      </div>

      {/* Search & Action Controls */}
      <div className="flex items-center gap-3">
        {/* Search Field */}
        <div ref={searchRef} className="relative w-60 sm:w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Search books, members, barcode..."
              className="w-full rounded-xl border border-[#2a241e] pl-9 pr-12 py-2 text-xs bg-[#201c17] text-stone-200 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 transition"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(-1);
              }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={(e) => {
                // Always prevent form submission on Enter
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (searchResults.length > 0 && highlightedIndex >= 0) {
                    handleSelectBook(searchResults[highlightedIndex]);
                  }
                  return;
                }

                if (!searchResults.length) return;

                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev < searchResults.length - 1 ? prev + 1 : 0
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : searchResults.length - 1
                  );
                } else if (e.key === "Escape") {
                  setShowDropdown(false);
                }
              }}
            />
            {searchLoading ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 animate-spin w-3.5 h-3.5" />
            ) : (
              <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-stone-500 border border-[#332b23] px-1.5 py-0.5 rounded bg-[#181512]">
                /
              </kbd>
            )}
          </div>

          {/* Typeahead Search Results Dropdown */}
          {showDropdown && searchTerm?.trim() !== "" && (
            <div className="absolute z-50 w-full bg-[#201c17] border border-[#2e2720] rounded-xl shadow-2xl mt-2 max-h-72 overflow-y-auto divide-y divide-[#231e18] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500">
              {!searchLoading && searchResults.length === 0 && (
                <div className="p-3 text-xs text-stone-400 text-center font-medium">
                  No catalog items found
                </div>
              )}
              {!searchLoading &&
                searchResults.map((book, index) => (
                  <div
                    key={book.id || index}
                    className={`flex items-center gap-3 p-2.5 cursor-pointer transition ${
                      index === highlightedIndex
                        ? "bg-[#28221c]"
                        : "hover:bg-[#25201a]"
                    }`}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={() => handleSelectBook(book)}
                  >
                    <div className="w-8 h-10 bg-[#161310] rounded overflow-hidden shrink-0 flex items-center justify-center text-[8px] text-stone-400">
                      {book.cover_url ? (
                        <img
                          src={book.cover_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span>BOOK</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-serif font-bold text-stone-200 truncate">
                        {book.title}
                      </p>
                      <p className="text-[10px] font-mono text-stone-500 tracking-wider">
                        BC: {book.barcode}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <button
          onClick={() => navigate("/library/cataloging")}
          className="w-8 h-8 rounded-lg bg-amber-500 text-stone-950 flex items-center justify-center hover:bg-amber-400 transition"
          title="Add New Book"
        >
          <Plus className="w-4 h-4 stroke-[2.5]" />
        </button>

        <button className="w-8 h-8 rounded-lg bg-[#201c17] border border-[#2e2720] text-stone-400 flex items-center justify-center hover:text-stone-200 transition">
          <Bell className="w-4 h-4" />
        </button>

        <button className="w-8 h-8 rounded-lg bg-[#201c17] border border-[#2e2720] text-stone-400 flex items-center justify-center hover:text-stone-200 transition">
          <Clock className="w-4 h-4" />
        </button>

        <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center font-bold text-xs shrink-0">
          {userInitials}
        </div>
      </div>
    </header>
  );
};